import { useNavigate } from "react-router-dom";
import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  PlusCircle,
  ShoppingCart,
  AlertTriangle,
  Package,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  Loader2,
  MapPin,
  Phone,
  Mail,
  ArrowRight,
} from "lucide-react";
import { all_routes } from "../../Router/all_routes";
import { API_BASE_URL } from "../../api/header";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";
import Modal from "@/components/ui/modal";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import RecordNotFound from "@/core/components/RecordNotFound";
import {
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Stores = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [selectedAlertData, setSelectedAlertData] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: null,
  });

  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  // Queries
  const { data: storesResponse, isLoading } = useQuery({
    queryKey: ["stores", currentPage, pageSize, searchText],
    queryFn: async () => {
      const res = await apiClient.get(`${API_BASE_URL}/store`, {
        params: {
          page: currentPage,
          per_page: pageSize,
          search: searchText || null,
        },
      });
      return res.data || { data: [], total: 0, last_page: 1 };
    },
  });

  const stores = storesResponse?.data?.data || storesResponse?.data || [];
  const totalItems =
    storesResponse?.meta?.total ||
    storesResponse?.total ||
    storesResponse?.data?.total ||
    stores.length;
  const lastPage =
    storesResponse?.meta?.last_page ||
    storesResponse?.last_page ||
    storesResponse?.data?.last_page ||
    1;

  // Reset page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  // Mutations
  const deleteStoreMutation = useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(`${API_BASE_URL}/store/${id}`);
    },
    onSuccess: () => {
      toast.success("Store deleted successfully");
      queryClient.invalidateQueries(["stores"]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to delete store");
    },
  });

  const checkAlertsMutation = useMutation({
    mutationFn: async (storeId) => {
      const res = await apiClient.get(
        `${API_BASE_URL}/inventory/analytics/low-stock-alerts`,
        {
          params: { store_id: storeId },
        },
      );
      return res.data;
    },
    onSuccess: (response) => {
      const findField = (obj, field) => {
        if (!obj || typeof obj !== "object") return null;
        if (field in obj) return obj[field];
        for (const key in obj) {
          const found = findField(obj[key], field);
          if (found !== null) return found;
        }
        return null;
      };

      const items = findField(response, "items") || [];
      const criticalCount = Number(
        findField(response, "critical_count") ||
          findField(response, "critical_alerts") ||
          items.filter((i) => i.urgency === "critical").length ||
          0,
      );
      const warningCount = Number(
        findField(response, "warning_count") ||
          findField(response, "warning_alerts") ||
          items.filter((i) => i.urgency === "warning").length ||
          0,
      );
      const totalAlerts = Number(
        findField(response, "total_alerts") ||
          items.length ||
          criticalCount + warningCount,
      );

      if (items.length > 0 || totalAlerts > 0) {
        setSelectedAlertData({
          items,
          total_alerts: totalAlerts,
          critical_alerts: criticalCount,
          warning_alerts: warningCount,
          alert_date:
            findField(response, "alert_date") || new Date().toISOString(),
          message: findField(response, "message") || "Inventory Alerts Found",
        });
        setShowAlertModal(true);
      } else {
        toast.success(
          findField(response, "message") || "No low stock items found",
        );
      }
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to check inventory alerts",
      );
    },
  });

  const handleDelete = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Store?",
      description: "You won't be able to revert this!",
      onConfirm: () => deleteStoreMutation.mutate(id),
    });
  };

  const handleEdit = (id) => {
    navigate(all_routes.editStore.replace(":id", id));
  };

  const handleCheckAlerts = (storeId) => {
    checkAlertsMutation.mutate(storeId);
  };

  const getStoreAddress = (store) => {
    const addressParts = [];
    if (store.address) addressParts.push(store.address);
    if (store.city) addressParts.push(store.city);
    if (store.state) addressParts.push(store.state);
    return addressParts.join(", ") || "-";
  };

  const getStatusBadgeVariant = (status) => {
    return status === "active" ? "success" : "destructive";
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => {
          const store = row.original;

          if (!hasPermission("store read")) {
            return <RecordNotFound />;
          }
          return (
            <div className="flex items-center gap-2">
              {store.logo && (
                <img
                  src={store.logo}
                  alt={store.name}
                  className="rounded w-10 h-10 object-contain"
                />
              )}
              <span className="font-medium">{store.name}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "phone",
        header: "Phone",
        cell: ({ row }) => (
          <span className="text-sm">{row.original.phone || "-"}</span>
        ),
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => (
          <span className="text-sm">{row.original.email || "-"}</span>
        ),
      },
      {
        accessorKey: "address",
        header: "Address",
        cell: ({ row }) => (
          <span className="text-sm">{getStoreAddress(row.original)}</span>
        ),
      },
      {
        accessorKey: "default_currency",
        header: "Currency",
        cell: ({ row }) => (
          <Badge variant="outline" className="font-normal">
            {row.original.default_currency?.code || "-"}
          </Badge>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge
            variant={getStatusBadgeVariant(row.original.status)}
            className="capitalize"
          >
            {row.original.status}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {hasPermission("store update") && (
                <DropdownMenuItem onClick={() => handleEdit(row.original.id)}>
                  <Edit className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => handleCheckAlerts(row.original.id)}
                disabled={checkAlertsMutation.isPending}
              >
                {checkAlertsMutation.isPending &&
                checkAlertsMutation.variables === row.original.id ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <AlertTriangle className="mr-2 h-4 w-4 text-yellow-600" />
                )}
                Check Alerts
              </DropdownMenuItem>
              {hasPermission("store delete") && (
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => handleDelete(row.original.id)}
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasPermission,
      checkAlertsMutation.isPending,
      checkAlertsMutation.variables,
    ],
  );

  return (
    <div className="page-wrapper">
      <div className="content p-6 max-w-[100vw] overflow-x-hidden">
        <PageHeader>
          <PageHeaderHeading
            title="Stores List"
            subtitle="Manage Your Stores"
          />
          <PageHeaderActions>
            {hasPermission("store create") && (
              <Button onClick={() => navigate(all_routes.newStore)}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Add New Store
              </Button>
            )}
          </PageHeaderActions>
        </PageHeader>

        <FilterCard>
          <FilterSearch
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search stores..."
          />
        </FilterCard>

        <TableCard>
          <DataTable
            columns={columns}
            data={stores}
            pagination={{
              pageIndex: currentPage - 1,
              pageSize: pageSize,
            }}
            pageCount={lastPage}
            onPaginationChange={(updater) => {
              if (typeof updater === "function") {
                const newState = updater({
                  pageIndex: currentPage - 1,
                  pageSize: pageSize,
                });
                setCurrentPage(newState.pageIndex + 1);
                setPageSize(newState.pageSize);
              } else {
                setCurrentPage(updater.pageIndex + 1);
                setPageSize(updater.pageSize);
              }
            }}
            isLoading={isLoading}
            enableRowSelection={true}
            footerInfo={
              <span className="font-medium text-foreground">
                Total Stores: {totalItems}
              </span>
            }
          />
        </TableCard>
      </div>

      {/* Inventory Alert Modal */}
      <Dialog open={showAlertModal} onOpenChange={setShowAlertModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto px-1">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Inventory Low Stock Alerts
            </DialogTitle>
          </DialogHeader>

          {selectedAlertData && (
            <div className="space-y-6 px-5 pb-5">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Total Alerts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-primary">
                      {selectedAlertData.total_alerts}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Critical
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-red-600">
                      {selectedAlertData.critical_alerts}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Warnings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-yellow-600">
                      {selectedAlertData.warning_alerts}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Alert Generated</AlertTitle>
                <AlertDescription>
                  {new Date(selectedAlertData.alert_date).toLocaleString()}
                </AlertDescription>
              </Alert>

              {/* Items Table */}
              {selectedAlertData.items &&
                selectedAlertData.items.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        Low Stock Items
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="rounded-md border overflow-x-auto">
                        <ShadcnTable>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Product / SKU</TableHead>
                              <TableHead>Urgency</TableHead>
                              <TableHead className="text-center">
                                Current Stock
                              </TableHead>
                              <TableHead className="text-center">
                                Suggested Order
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedAlertData.items.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-muted rounded">
                                      <Package className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                    <div>
                                      <div className="font-medium">
                                        {item.product_name}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        SKU: {item.sku || "N/A"}
                                      </div>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant={
                                      item.urgency === "critical"
                                        ? "destructive"
                                        : "warning"
                                    }
                                    className="uppercase"
                                  >
                                    {item.urgency || "LOW STOCK"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="flex flex-col">
                                    <span className="text-lg font-bold">
                                      {item.current_quantity ?? 0}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      Reorder at: {item.reorder_level || 0}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center gap-2">
                                    <ArrowRight className="h-4 w-4 text-primary" />
                                    <span className="text-lg font-bold text-primary">
                                      {item.suggested_order_quantity || 0}
                                    </span>
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    Recommended
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </ShadcnTable>
                      </div>
                    </CardContent>
                  </Card>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Modal
        open={confirmDialog.isOpen}
        onOpenChange={(open) =>
          setConfirmDialog((prev) => ({ ...prev, isOpen: open }))
        }
        title={confirmDialog.title}
        maxWidth="max-w-md"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() =>
                setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
              }
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                confirmDialog.onConfirm?.();
                setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
              }}
            >
              Delete
            </Button>
          </div>
        }
      >
        <div className="py-4">
          <p className="text-sm text-muted-foreground font-medium">
            {confirmDialog.description}
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Stores;
